import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertCircle, Clock, Link2, Wallet, TrendingUp, TrendingDown,
  FlaskConical, Lock, Sparkles, ArrowRight, Star, Shield, Layers,
  Wand2, LineChart, Timer, Crown, Filter, Grid3x3, History,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useHome } from '@/hooks/useHome'
import { usePicks, useParlays } from '@/hooks/usePicks'
import { useChallenges, useDailyRunToday } from '@/hooks/useStrategies'
import { usePersonalBankroll } from '@/hooks/usePersonalBankroll'
import { useMyIntelligence } from '@/hooks/useBlackIntelligence'
import { PickDetailSheet } from '@/components/PickDetailSheet'
import { ChallengeHomeCard } from '@/components/ChallengeHomeCard'
import { QuinielaHomeCard } from '@/components/quiniela/QuinielaHomeCard'
import { ResponsibleNotice } from '@/components/ResponsibleNotice'
import { StatusBadge } from '@/components/StatusBadge'
import { SkeletonCard } from '@/components/LoadingSkeleton'
import { ErrorState } from '@/components/ErrorState'
import { TeamCrest } from '@/components/TeamCrest'
import { PillTabs } from '@/components/PillTabs'
import { formatOdds, liveScoreLabel, probabilityDisplay, resolvePickBadgeStatus, cn } from '@/lib/utils'
import { parlayDisplayStatus } from '@/lib/badges'
import type { PickLeg, Parlay } from '@/types/miniApp'

// Home Premium + Lifecycle (01/10/2026, Sección 6 del encargo): MISMA etiqueta/orden que
// `ParlaysPage.tsx::STANDARD_TABS` -- nunca una segunda fuente de verdad para el nombre
// comercial de cada categoría.
const PARLAY_HOME_LABELS: Record<string, string> = {
  conservador: 'JA Duo', balanceado: 'JA Triple', agresivo: 'JA Quad',
}

function formatMoney(value: number | undefined | null): string {
  if (value == null) return 'N/D'
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value)
}

function todayLabel(iso: string): string {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
}

/** HERO -- P0.56 (Sección 6 del encargo): la pregunta que Home debe responder de inmediato
 * es "¿qué tiene John Anthony para mí hoy?", con la Top Oportunidad como thesis visual --
 * nunca una card más entre iguales. Cuota/probabilidad en `font-mono` (identidad "terminal
 * de precisión"), narrativa breve como dek editorial, nunca solo un badge. */
/** Cuenta atrás real hasta un kickoff -- hook compartido (antes duplicado entre
 * `OpportunityHero`/`NextPickCountdown`, P1.1 los fusiona en una sola card). Se recalcula
 * cada segundo desde `kickoff_utc` real, nunca un cron interno inventado. `null` cuando ya
 * empezó o no hay kickoff conocido -- el llamador decide qué mostrar en ese caso. */
function useCountdown(kickoffUtc: string | null | undefined): { hours: number; minutes: number; seconds: number } | null {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  if (!kickoffUtc) return null
  const diffMs = new Date(kickoffUtc).getTime() - now
  if (diffMs <= 0) return null
  const totalSeconds = Math.floor(diffMs / 1000)
  return { hours: Math.floor(totalSeconds / 3600), minutes: Math.floor((totalSeconds % 3600) / 60), seconds: totalSeconds % 60 }
}

/** "Próximo Pick Oficial" -- Sección 3 del encargo (P1.1): fusiona el hero de "Top
 * Oportunidad" (datos completos: logos/selección/mercado/cuota/probabilidad/confianza) con
 * el countdown real que antes vivía aparte (`NextPickCountdown`) en UNA sola card grande,
 * igual que el mockup aprobado -- nunca dos cards separadas compitiendo por el mismo rol.
 * `categoryLabel` viene del llamador (de qué lista real salió: Top IA/Más Seguros/
 * Secundarios), nunca inferido aquí. Cuando el kickoff pasa, este pick deja de ser "next"
 * (el llamador ya lo excluye de `nextPick`) -- el siguiente PENDIENTE ocupa el bloque
 * automáticamente en el siguiente render, nunca un hueco vacío mientras haya alguno. */
function NextPickHero({ pick, categoryLabel, onSelect }: { pick: PickLeg; categoryLabel: string; onSelect: () => void }) {
  const prob = probabilityDisplay(pick.model_probability, pick.probability_is_calibrated)
  const homeName = pick.home_team ?? 'Local'
  const awayName = pick.away_team ?? 'Visitante'
  const countdown = useCountdown(pick.kickoff_utc)

  return (
    <section
      onClick={onSelect}
      className="group relative overflow-hidden rounded-3xl border border-brand/30 bg-gradient-to-b from-brand-soft/20 via-panel to-panel p-6 md:p-9 cursor-pointer transition-colors hover:border-brand/50"
    >
      <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-brand/10 blur-3xl pointer-events-none" />
      <div className="relative space-y-5">
        <div className="flex items-center justify-between gap-3">
          <span className="eyebrow text-brand">Próximo Pick Oficial</span>
          {countdown && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold text-brand shrink-0">
              <Timer className="h-3.5 w-3.5" />
              {countdown.hours > 0
                ? `${countdown.hours}:${twoDigits(countdown.minutes)}:${twoDigits(countdown.seconds)}`
                : `${twoDigits(countdown.minutes)}:${twoDigits(countdown.seconds)}`}
            </span>
          )}
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="space-y-2">
            <span className="inline-block px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider bg-brand-soft/60 text-brand border border-brand/30">
              {categoryLabel}
            </span>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2.5">
                <TeamCrest src={pick.home_team_logo} alt={homeName} size={40} />
                <span className="font-display text-sm font-semibold text-text">{homeName}</span>
              </div>
              <span className="font-mono text-xs text-text-dim">vs</span>
              <div className="flex items-center gap-2.5">
                <TeamCrest src={pick.away_team_logo} alt={awayName} size={40} />
                <span className="font-display text-sm font-semibold text-text">{awayName}</span>
              </div>
            </div>
          </div>

          <div className="flex items-end gap-8">
            <div>
              <p className="text-[10px] font-bold uppercase text-text-dim">Selección</p>
              <p className="font-display text-lg md:text-xl font-bold text-text leading-tight">
                {pick.selection_label ?? pick.selection}
              </p>
              <p className="text-[11px] text-text-dim">{pick.market_label ?? pick.market}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10px] font-bold uppercase text-text-dim">Cuota</p>
              <p className="figure text-4xl md:text-5xl font-bold text-brand leading-none">{formatOdds(pick.odds)}</p>
            </div>
          </div>
        </div>

        <div className="hairline pt-4 flex flex-wrap items-center gap-x-6 gap-y-1.5">
          {prob.kind !== 'unknown' && (
            <span className="text-xs text-text-muted">
              Probabilidad estimada <strong className="figure text-text">{prob.label}</strong>
            </span>
          )}
          {pick.confidence_score != null && (
            <span className="text-xs text-text-muted">
              Confianza <strong className="text-text">{pick.confidence_score >= 75 ? 'Alta' : pick.confidence_score >= 50 ? 'Media' : 'Baja'}</strong>
            </span>
          )}
          {pick.risk_level && (
            <span className="text-xs text-text-muted">
              Riesgo <strong className="text-text capitalize">{pick.risk_level.replace('_', ' ')}</strong>
            </span>
          )}
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); onSelect() }}
          className="w-full py-3 rounded-xl bg-brand text-black text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5 group-hover:bg-accent-hover transition-colors"
        >
          Ver análisis completo <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </section>
  )
}

/** Estado coherente cuando ya no queda ningún pick pendiente hoy -- Sección 3 del encargo:
 * "si no queda ninguno, mostrar un estado premium coherente, no un hueco vacío". El
 * portafolio del día NUNCA se borra (`ParlaysOfficialList`/picks del día siguen visibles
 * más abajo) -- esto solo reemplaza el bloque de countdown cuando ya no aplica. */
function NoMorePendingPicks() {
  return (
    <section className="card p-5 rounded-2xl text-center space-y-1 border-brand/20">
      <p className="text-sm font-bold text-text">Todos los picks de hoy ya comenzaron</p>
      <p className="text-xs text-text-dim">Revisa su estado en vivo en Actividad Reciente, más abajo.</p>
    </section>
  )
}

/** Fila compacta de datos -- reemplaza las cards uniformes por una lista tipo "order book"
 * para Más Segura / Secundarios (Sección 6/20: nunca card-card-card idéntica). */
function PickRow({ pick, onSelect }: { pick: PickLeg; onSelect: () => void }) {
  return (
    <button onClick={onSelect} className="data-row w-full text-left hover:bg-panel-elevated/30 -mx-1 px-1 rounded-lg transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex -space-x-2 shrink-0">
          <TeamCrest src={pick.home_team_logo} alt={pick.home_team ?? ''} size={22} />
          <TeamCrest src={pick.away_team_logo} alt={pick.away_team ?? ''} size={22} />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-text truncate">{pick.selection_label ?? pick.selection}</p>
          <p className="text-[10px] text-text-dim truncate">{pick.home_team} vs {pick.away_team}</p>
        </div>
      </div>
      <span className="figure text-sm font-bold text-text shrink-0">{formatOdds(pick.odds)}</span>
    </button>
  )
}

/** Franja de transparencia -- P0.56 (Sección 6): prueba de trabajo del motor, SIEMPRE
 * secundaria/compacta, nunca protagonista (nunca vuelve a ser la primera cosa que se ve). */
function TransparencyStrip({
  reviewed, evaluated, candidates, discarded,
}: { reviewed?: number | null; evaluated?: number | null; candidates?: number | null; discarded?: number | null }) {
  if (reviewed == null && evaluated == null) return null
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[10px] text-text-dim/80 font-mono">
      {reviewed != null && <span>{reviewed} partidos analizados</span>}
      {evaluated != null && <span>{evaluated.toLocaleString('es-ES')} posibilidades evaluadas</span>}
      {candidates != null && <span>{candidates} candidatos</span>}
      {discarded != null && <span>{discarded} descartados</span>}
    </div>
  )
}

/** Sprint "Product Experience V2" (10/09/2026): "Picks de hoy" -- primera pregunta real que
 * el mockup aprobado responde antes que cualquier otra cosa: cuánto tiene John Anthony
 * disponible HOY, desglosado por producto. Cifras 100% derivadas de los mismos datos que ya
 * carga Inicio (`usePicks`/`useParlays`) -- nunca un conteo nuevo/paralelo. */
function TodaySummaryStrip({
  topIA, safest, secondary, sgp, parlaysCount, challengePicksCount,
}: {
  topIA: number; safest: number; secondary: number; sgp: number
  parlaysCount: number; challengePicksCount: number
}) {
  const total = topIA + safest + secondary + sgp
  if (total === 0) return null
  const items: { label: string; value: number; icon: typeof Star }[] = [
    { label: 'Top IA', value: topIA, icon: Star },
    { label: 'Más Seguros', value: safest, icon: Shield },
    { label: 'Secundarios', value: secondary, icon: Layers },
    { label: 'SGP', value: sgp, icon: Wand2 },
  ]
  // Sección 2 del encargo (P1.1): fila compacta adicional -- Parlays/Reto JA,
  // MISMOS datos ya usados más abajo en sus propias secciones (nunca un conteo paralelo).
  const secondaryItems: { label: string; value: number; sub: string; to: string; icon: typeof Link2 }[] = [
    { label: 'Parlays', value: parlaysCount, sub: 'Duo · Triple · Quad', to: '/parlays', icon: Link2 },
    { label: 'Reto JA', value: challengePicksCount, sub: 'En curso', to: '/reto', icon: Crown },
  ]
  return (
    <section className="card p-4 rounded-2xl space-y-3">
      <div className="flex items-baseline justify-between">
        <p className="eyebrow">Resumen del día</p>
        <p className="figure text-2xl font-bold text-brand leading-none">{total} <span className="text-xs font-bold text-text-dim">picks oficiales</span></p>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {items.map(({ label, value, icon: Icon }) => (
          <div key={label} className="flex flex-col items-center gap-1 text-center">
            <Icon className="h-3.5 w-3.5 text-text-dim" />
            <span className="figure text-sm font-bold text-text">{value}</span>
            <span className="text-[9px] font-bold text-text-dim uppercase tracking-wide leading-tight">{label}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 pt-1">
        {secondaryItems.map(({ label, value, sub, to, icon: Icon }) => (
          <Link key={label} to={to} className="flex flex-col gap-1 p-2 rounded-xl bg-panel-elevated/50 border border-border/40 hover:border-brand/30 transition">
            <Icon className="h-3 w-3 text-text-dim" />
            <span className="figure text-sm font-bold text-text">{value}</span>
            <span className="text-[9px] font-bold text-text-dim uppercase tracking-wide leading-tight">{label}</span>
            <span className="text-[8px] text-text-dim/70 leading-tight">{sub}</span>
          </Link>
        ))}
      </div>
    </section>
  )
}

/** "Parlays Oficiales" -- Sección 6 del encargo: JA Duo/Triple/Quad, uno por fila, con su
 * lifecycle real (PENDIENTE → EN JUEGO → ACERTÓ/FALLÓ/VOID, `parlayDisplayStatus`, misma
 * lectura canónica que `ParlayCard.tsx`). BUG REAL corregido (causa raíz de "Parlays 0 --
 * Sin combinadas hoy" pese a existir 3 publicados hoy): el filtro anterior excluía cualquier
 * parlay con `is_bettable === false` -- ese campo se recalcula en VIVO en cuanto el primer
 * fixture arranca, así que un Duo/Triple/Quad real publicado hoy desaparecía del Home en el
 * instante en que empezaba a jugarse. Un parlay publicado PERSISTE durante todo su lifecycle;
 * solo `no_formado` se distingue honestamente (nunca se inventa un Quad que el motor no
 * formó, nunca se confunde con uno que "desapareció"). */
function ParlaysOfficialList({ parlays, onOpenParlays }: { parlays: Parlay[]; onOpenParlays: () => void }) {
  const byType: Record<string, Parlay | undefined> = {}
  for (const p of parlays) {
    if (p.parlay_type && p.parlay_type in PARLAY_HOME_LABELS && !byType[p.parlay_type]) {
      byType[p.parlay_type] = p
    }
  }
  const rows = Object.keys(PARLAY_HOME_LABELS)
    .map((type) => ({ type, label: PARLAY_HOME_LABELS[type], parlay: byType[type] }))
    .filter((row) => row.parlay != null)
  if (rows.length === 0) return null

  return (
    <section className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 eyebrow text-parlay">
          <Link2 className="h-3.5 w-3.5" /> Parlays Oficiales
        </div>
        <button onClick={onOpenParlays} className="text-[10px] font-black uppercase tracking-wider text-text-dim hover:text-brand">
          Ver todos →
        </button>
      </div>
      <div className="card p-3 divide-y divide-border/50">
        {rows.map(({ type, label, parlay }) => {
          const p = parlay as Parlay
          const formed = p.status !== 'no_formado'
          const displayStatus = formed ? parlayDisplayStatus(p.status, p.is_bettable) : null
          return (
            <button key={type} onClick={onOpenParlays} className="data-row w-full text-left hover:bg-panel-elevated/30 -mx-1 px-1 rounded-lg transition-colors">
              <div className="min-w-0">
                <p className="text-xs font-bold text-text">{label}</p>
                <p className="text-[10px] text-text-dim">
                  {formed ? `${p.n_legs ?? p.legs.length} selecciones` : 'No formado hoy'}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {formed && <span className="figure text-sm font-bold text-text">@{formatOdds(p.combined_odds)}</span>}
                {displayStatus ? (
                  <StatusBadge status={displayStatus} />
                ) : (
                  <span className="text-[9px] font-bold uppercase text-text-dim px-2 py-0.5 rounded-full border border-border/50">
                    No formado
                  </span>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </section>
  )
}

function twoDigits(n: number): string {
  return n.toString().padStart(2, '0')
}

/** Mi Evolución -- vive junto a Mi Banca (mismo par que el mockup aprobado muestra en
 * Inicio), MISMOS datos ya calculados por `PersonalBankrollService.compute_metrics` (`roi`) --
 * nunca un cálculo nuevo aquí, solo un resumen que enlaza a la pantalla completa existente. */
function EvolutionSummaryStrip() {
  const { data: bankroll } = usePersonalBankroll()
  if (!bankroll?.configured || bankroll.roi == null) return null
  const positive = bankroll.roi >= 0
  return (
    <Link to="/mas/evolucion" className="card p-4 rounded-2xl space-y-2.5 block hover:border-brand/30 transition">
      <div className="flex items-center gap-1.5 eyebrow">
        <LineChart className="h-3.5 w-3.5" /> Mi Evolución
      </div>
      <div>
        <p className="text-[9px] font-bold uppercase text-text-dim">ROI</p>
        <p className={cn('figure text-base font-bold flex items-center gap-1', positive ? 'text-success' : 'text-danger')}>
          {positive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
          {bankroll.roi.toFixed(2)}%
        </p>
      </div>
    </Link>
  )
}

function PersonalSummaryStrip() {
  const { data: bankroll } = usePersonalBankroll()
  if (!bankroll?.configured) return null
  const positive = (bankroll.betting_profit_loss ?? 0) >= 0
  return (
    <Link to="/banca" className="card p-4 rounded-2xl space-y-2.5 block hover:border-brand/30 transition">
      <div className="flex items-center gap-1.5 eyebrow">
        <Wallet className="h-3.5 w-3.5" /> Mi Banca
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[9px] font-bold uppercase text-text-dim">Saldo</p>
          <p className="figure text-base font-bold text-brand">{formatMoney(bankroll.current_balance)}</p>
        </div>
        <div className="text-right">
          <p className="text-[9px] font-bold uppercase text-text-dim">P/L Deportivo</p>
          <p className={cn('figure text-base font-bold flex items-center justify-end gap-1', positive ? 'text-success' : 'text-danger')}>
            {positive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
            {formatMoney(bankroll.betting_profit_loss)}
          </p>
        </div>
      </div>
    </Link>
  )
}

/** JA Lab teaser -- P0.56 (Sección 6/17 del encargo): BLACK ve la invitación real a
 * analizar un partido propio; PRO ve la misma capacidad, visiblemente locked, con CTA a
 * BLACK; FREE la descubre sin competir con el mensaje FREE→PRO (tono más discreto). */
function JALabTeaser({ plan }: { plan: string }) {
  if (plan === 'BLACK' || plan === 'ADMIN') {
    return (
      <Link to="/ja-lab" className="card p-4 rounded-2xl space-y-2 border-exact/25 block hover:border-exact/40 transition">
        <div className="flex items-center gap-1.5 eyebrow text-exact">
          <FlaskConical className="h-3.5 w-3.5" /> John Anthony Lab
        </div>
        <p className="text-sm font-semibold text-text">¿Hay un partido que quieres analizar?</p>
        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-exact pt-1">
          Analizar partido <ArrowRight className="h-3 w-3" />
        </span>
      </Link>
    )
  }
  if (plan === 'PRO') {
    return (
      <Link to="/ja-lab" className="card p-4 rounded-2xl space-y-2 border-border/60 block hover:border-exact/30 transition">
        <div className="flex items-center gap-1.5 eyebrow text-text-dim">
          <Lock className="h-3.5 w-3.5" /> John Anthony Lab
        </div>
        <p className="text-sm font-semibold text-text">Analiza el partido que tú elijas</p>
        <p className="text-xs text-text-muted">Exclusivo BLACK.</p>
        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-exact pt-1">
          Descubrir BLACK <ArrowRight className="h-3 w-3" />
        </span>
      </Link>
    )
  }
  return (
    <Link to="/ja-lab" className="block text-[11px] text-text-dim hover:text-text-muted transition px-1">
      John Anthony Lab: analiza cualquier partido — exclusivo BLACK →
    </Link>
  )
}

/** Inteligencia personal -- máximo 1 insight, o progreso real hacia el umbral (Sección 6:
 * nunca "sin datos" a secas). Umbral central 10 (`best_category_insight`,
 * `intelligence_service.py::MIN_SAMPLE`) -- el mismo número que da forma al insight más
 * sustancial, nunca inventado. */
function BlackHomeInsight() {
  const { data, isLoading } = useMyIntelligence()
  if (isLoading || !data) return null

  const topInsight = data.insights?.[0]
  const totalBets = data.total_bets ?? 0
  const threshold = 10

  return (
    <div className="card p-4 rounded-2xl space-y-2 border-exact/25">
      <div className="flex items-center gap-1.5 eyebrow text-exact">
        <Sparkles className="h-3.5 w-3.5" /> Inteligencia BLACK
      </div>
      {topInsight ? (
        <>
          <p className="text-sm font-semibold text-text">{topInsight.title}</p>
          <p className="text-xs text-text-muted leading-relaxed">{topInsight.message}</p>
        </>
      ) : (
        <>
          <p className="text-xs text-text-muted leading-relaxed">
            John Anthony está aprendiendo de tus decisiones.
          </p>
          <div className="pt-1 space-y-1">
            <div className="flex items-center justify-between text-[10px] font-bold text-text-dim">
              <span className="figure">{Math.min(totalBets, threshold)} / {threshold} apuestas registradas</span>
            </div>
            <div className="h-1.5 rounded-full bg-panel-elevated overflow-hidden">
              <div
                className="h-full rounded-full bg-exact transition-all"
                style={{ width: `${Math.min(100, (totalBets / threshold) * 100)}%` }}
              />
            </div>
          </div>
        </>
      )}
      <Link to="/mas/evolucion" className="inline-block text-[10px] font-black uppercase tracking-wider text-exact pt-1">
        Ver mi evolución →
      </Link>
    </div>
  )
}

export function HomePage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { data, isLoading, isError, error, refetch } = useHome(true)
  const { data: picks } = usePicks(true)
  const { data: parlays } = useParlays(true)
  const { data: dailyRun } = useDailyRunToday()
  const { data: challenges } = useChallenges()
  const officials = challenges?.filter((c) => c.is_official) ?? []
  const officialChallenge = officials.find((c) => c.status === 'ACTIVE') ?? officials[0] ?? null
  const [selectedPick, setSelectedPick] = useState<PickLeg | null>(null)
  const [picksTab, setPicksTab] = useState<'top_ia' | 'safest' | 'secondary'>('top_ia')
  const [activityTab, setActivityTab] = useState<'todos' | 'en_juego' | 'acertados' | 'fallados'>('todos')

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="h-14 bg-panel-elevated/40 rounded-2xl animate-pulse" />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="p-4">
        <ErrorState error={error} onRetry={() => refetch()} />
      </div>
    )
  }

  const trialDaysRemaining =
    data.subscription.plan === 'FREE' && data.subscription.status === 'active' && !data.trial_expired && data.subscription.expires_at
      ? Math.ceil((new Date(data.subscription.expires_at).getTime() - Date.now()) / 86_400_000)
      : null

  const featured = picks?.featured ?? []
  // Home Premium + Lifecycle (01/10/2026, Sección 6 del encargo) -- BUG REAL corregido:
  // filtrar por `is_bettable !== false` (P0.2) excluía CUALQUIER parlay en cuanto su primer
  // fixture arrancaba (is_bettable se recalcula en vivo vía `is_fixture_bettable()`), así que
  // un Duo/Triple/Quad real publicado hoy desaparecía del Home ("Parlays 0 -- Sin combinadas
  // hoy") pese a existir. Un parlay publicado persiste durante TODO su lifecycle (pendiente →
  // en juego → acertó/falló/void) -- `status` YA es el campo real de lifecycle (se actualiza
  // con la liquidación real, nunca con el paso del tiempo); solo `no_formado` significa
  // "el motor no formó esta categoría hoy", el único caso que de verdad debe excluirse del
  // conteo de "disponibles".
  const validParlays = (parlays?.parlays ?? []).filter((p) => p.status !== 'no_formado')
  const validSGP = validParlays.filter((p) => p.parlay_type === 'same_game_parlay')
  const hasBankroll = data.plan_features.bankroll
  const hasPersonalIntelligence = data.plan_features.personal_intelligence
  const userPlan = data.subscription.plan ?? user?.plan ?? 'FREE'

  // "Próximo pick" -- el kickoff más cercano entre los picks de hoy todavía no comenzados,
  // real (nunca un cron interno inventado). `categoryLabel` viaja junto al pick (de qué
  // lista real salió) para que `NextPickHero` lo muestre sin inferir nada.
  const secondaryPicks = picks?.secondary ?? []
  const allTodayPicks: Array<{ pick: PickLeg; categoryLabel: string }> = [
    ...featured.map((pick) => ({ pick, categoryLabel: 'Top IA' })),
    ...(picks?.safest_top_picks ?? []).map((pick) => ({ pick, categoryLabel: 'Más Seguros' })),
    ...secondaryPicks.map((pick) => ({ pick, categoryLabel: 'Secundario' })),
  ]
  const nowTs = Date.now()
  const nextPickEntry = allTodayPicks
    .filter((e) => e.pick.kickoff_utc && new Date(e.pick.kickoff_utc).getTime() > nowTs)
    .sort((a, b) => new Date(a.pick.kickoff_utc as string).getTime() - new Date(b.pick.kickoff_utc as string).getTime())[0] ?? null

  const picksTabItems = [
    { key: 'top_ia', label: 'Top IA', count: featured.length },
    { key: 'safest', label: 'Más Seguros', count: (picks?.safest_top_picks ?? []).length },
    { key: 'secondary', label: 'Secundarios', count: secondaryPicks.length },
  ]
  const activePicksList = picksTab === 'top_ia' ? featured : picksTab === 'safest' ? (picks?.safest_top_picks ?? []) : secondaryPicks

  const activityCounts = {
    todos: data.recent_activity.length,
    en_juego: data.recent_activity.filter((a) => a.fixture_status === 'live').length,
    acertados: data.recent_activity.filter((a) => a.status === 'ganada').length,
    fallados: data.recent_activity.filter((a) => a.status === 'perdida').length,
  }
  const filteredActivity = data.recent_activity.filter((a) => {
    if (activityTab === 'todos') return true
    if (activityTab === 'en_juego') return a.fixture_status === 'live'
    if (activityTab === 'acertados') return a.status === 'ganada'
    return a.status === 'perdida'
  })

  return (
    <div className="p-4 space-y-7 lg:max-w-7xl lg:mx-auto">
      {/* Saludo / fecha */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold text-text">
            Hola, {data.display_name ?? user?.telegram_username ?? 'de nuevo'}
          </h2>
          <p className="text-xs text-text-dim capitalize">{todayLabel(data.date)}</p>
        </div>
      </div>

      {/* "Qué tiene el usuario disponible HOY" -- primera lectura antes que cualquier otra
          cosa (mockup aprobado, Sección "Inicio"): conteo por producto, próximo kickoff real,
          y el par Mi Banca/Mi Evolución. */}
      <TodaySummaryStrip
        topIA={featured.length}
        safest={(picks?.safest_top_picks ?? []).length}
        secondary={secondaryPicks.length}
        sgp={validSGP.length}
        parlaysCount={validParlays.length}
        challengePicksCount={officialChallenge?.current_step ?? 0}
      />
      {(hasBankroll || hasPersonalIntelligence) && (
        <div className="grid grid-cols-2 gap-3">
          {hasBankroll && <PersonalSummaryStrip />}
          {hasPersonalIntelligence && <EvolutionSummaryStrip />}
        </div>
      )}

      {data.subscription.status !== 'active' && (
        <div className="card border-warn/30 bg-warn-soft/20 flex items-center gap-2.5 p-3 rounded-2xl">
          <AlertCircle className="h-4 w-4 text-warn shrink-0 animate-pulse" />
          <p className="text-xs font-semibold text-warn leading-tight">
            Tu suscripción está en estado &quot;{data.subscription.status}&quot;.
          </p>
        </div>
      )}

      {data.trial_expired && (
        <div className="card border-brand/30 bg-brand/10 space-y-2 p-4 rounded-2xl">
          <div className="flex items-center gap-2.5">
            <Clock className="h-4 w-4 text-brand shrink-0" />
            <p className="text-sm font-bold text-text">Tu prueba gratuita de 7 días ha terminado</p>
          </div>
          <p className="text-xs text-text-muted leading-relaxed">
            Pasa a <span className="font-semibold text-text">PRO (14,99 €)</span> o{' '}
            <span className="font-semibold text-text">BLACK (39,99 €)</span> para recuperar tu
            cartera del día completa, parlays y banca.
          </p>
          <Link to="/mas/suscripcion" className="inline-block px-4 py-2 rounded-lg bg-brand text-black text-sm font-semibold">
            Ver planes
          </Link>
        </div>
      )}

      {trialDaysRemaining != null && trialDaysRemaining > 0 && (
        <div className="card border-border/60 bg-panel-elevated/30 flex items-center justify-between gap-3 p-3 rounded-2xl">
          <p className="text-[11px] text-text-dim">
            Te quedan <span className="font-bold text-text">{trialDaysRemaining} {trialDaysRemaining === 1 ? 'día' : 'días'}</span> de prueba gratuita.
          </p>
          <Link to="/mas/suscripcion" className="shrink-0 text-[10px] font-black uppercase tracking-wider text-brand">
            Ver planes
          </Link>
        </div>
      )}

      <div className="lg:grid lg:grid-cols-3 lg:gap-6 lg:items-start space-y-7 lg:space-y-0">
        <div className="lg:col-span-2 space-y-7">
          {/* Próximo Pick Oficial -- Sección 3 del encargo (P1.1): hero grande fusionado
              (ver docstring de `NextPickHero`). Cuando no queda ninguno pendiente, estado
              premium coherente (Sección 3: "no un hueco vacío") -- el portafolio completo
              sigue visible más abajo, nunca se borra. */}
          {nextPickEntry ? (
            <NextPickHero
              pick={nextPickEntry.pick}
              categoryLabel={nextPickEntry.categoryLabel}
              onSelect={() => setSelectedPick(nextPickEntry.pick)}
            />
          ) : (
            <NoMorePendingPicks />
          )}

          {/* Picks del Día -- Sección 4 del encargo (P1.1): tabs Top IA/Más Seguros/
              Secundarios con conteo real, reemplaza la lista plana anterior. */}
          {featured.length + (picks?.safest_top_picks ?? []).length + secondaryPicks.length > 0 && (
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="eyebrow">Picks del Día</p>
                <Link to="/picks" className="text-[10px] font-black uppercase tracking-wider text-text-dim hover:text-brand">
                  Ver todos ({featured.length + (picks?.safest_top_picks ?? []).length + secondaryPicks.length}) →
                </Link>
              </div>
              <PillTabs items={picksTabItems} active={picksTab} onChange={(k) => setPicksTab(k as typeof picksTab)} />
              {activePicksList.length > 0 ? (
                <div className="card p-3 divide-y divide-border/50">
                  {activePicksList.map((pick) => (
                    <PickRow key={pick.decision_record_id} pick={pick} onSelect={() => setSelectedPick(pick)} />
                  ))}
                </div>
              ) : (
                <div className="card p-4 text-center">
                  <p className="text-xs text-text-dim">Sin picks en esta categoría hoy.</p>
                </div>
              )}
            </section>
          )}

          {/* Parlays Oficiales -- Sección 5 del encargo: JA Duo/Triple/Quad con su lifecycle
              real, nunca solo un contador. Ver docstring de `ParlaysOfficialList`. */}
          {validParlays.length > 0 ? (
            <ParlaysOfficialList parlays={validParlays} onOpenParlays={() => navigate('/parlays')} />
          ) : (
            <Link to="/parlays" className="card p-4 rounded-2xl space-y-1 block hover:border-parlay/40 transition">
              <div className="flex items-center gap-1.5 eyebrow text-parlay">
                <Link2 className="h-3.5 w-3.5" /> Parlays
              </div>
              <p className="text-xs text-text-dim">Sin combinadas hoy</p>
            </Link>
          )}

          {/* Transparencia -- prueba de trabajo compacta, siempre secundaria */}
          <TransparencyStrip
            reviewed={dailyRun?.n_fixtures_reviewed}
            evaluated={dailyRun?.n_markets_evaluated}
            candidates={dailyRun?.n_candidates}
            discarded={dailyRun?.n_discarded}
          />

          {/* Actividad reciente / LIVE -- Sección 7 del encargo (P1.1): tabs Todos/En juego/
              Acertados/Fallados con conteo real -- MISMA lectura canónica que antes
              (`resolvePickBadgeStatus`), nunca un marcador/minuto inventado. */}
          {data.recent_activity.length > 0 && (
            <section className="space-y-2">
              <div className="flex items-center gap-1.5 eyebrow">
                <Filter className="h-3.5 w-3.5" /> Actividad Reciente
              </div>
              <PillTabs
                items={[
                  { key: 'todos', label: 'Todos', count: activityCounts.todos },
                  { key: 'en_juego', label: 'En juego', count: activityCounts.en_juego },
                  { key: 'acertados', label: 'Acertados', count: activityCounts.acertados },
                  { key: 'fallados', label: 'Fallados', count: activityCounts.fallados },
                ]}
                active={activityTab}
                onChange={(k) => setActivityTab(k as typeof activityTab)}
              />
              {filteredActivity.length > 0 ? (
                <div className="card p-3 divide-y divide-border/40">
                  {filteredActivity.slice(0, 10).map((item, index) => {
                    const badgeStatus = resolvePickBadgeStatus(item.fixture_status, item.status)
                    const isLive = item.fixture_status === 'live'
                    const liveScore = isLive ? liveScoreLabel(item.home_score, item.away_score) : null
                    const finalScore = !isLive && item.home_score != null && item.away_score != null
                      ? `${item.home_score}-${item.away_score}` : null
                    return (
                      <div key={`${item.fixture_id}-${index}`} className="data-row">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-text truncate">
                            {item.match_label}
                            {liveScore && <span className="text-brand font-mono font-bold"> {liveScore}</span>}
                            {finalScore && <span className="text-text-dim font-normal"> ({finalScore})</span>}
                          </p>
                          <p className="text-[10px] text-text-dim mt-0.5">
                            {item.selection_label ?? item.selection} · <span className="figure">{formatOdds(item.odds_taken)}</span>
                          </p>
                        </div>
                        {badgeStatus && <StatusBadge status={badgeStatus} />}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="card p-4 text-center">
                  <p className="text-xs text-text-dim">Sin actividad en este filtro.</p>
                </div>
              )}
            </section>
          )}

          <ResponsibleNotice />
        </div>

        {/* Columna secundaria (desktop) / bloques siguientes (mobile) -- Mi Banca/Mi
            Evolución ya se muestran arriba (par de cards de "qué tengo hoy"), nunca
            duplicados aquí. */}
        <div className="space-y-4">
          <QuinielaHomeCard />
          <ChallengeHomeCard challenge={officialChallenge} />
          <JALabTeaser plan={userPlan} />
          {hasPersonalIntelligence && <BlackHomeInsight />}
        </div>
      </div>

      {/* Acceso Rápido -- Sección 13 del encargo: solo destinos que existen de verdad en la
          navegación real, nunca una ruta ficticia. */}
      <section className="space-y-2">
        <div className="flex items-center gap-1.5 eyebrow">
          <Grid3x3 className="h-3.5 w-3.5" /> Acceso Rápido
        </div>
        <div className="grid grid-cols-4 gap-2.5">
          {[
            { label: 'Picks', to: '/picks', icon: Star, color: 'text-brand' },
            { label: 'Parlays', to: '/parlays', icon: Link2, color: 'text-parlay' },
            { label: 'Reto JA', to: '/reto', icon: Crown, color: 'text-exact' },
            { label: 'Mi Banca', to: '/banca', icon: Wallet, color: 'text-brand' },
            { label: 'Mi Evolución', to: '/mas/evolucion', icon: LineChart, color: 'text-brand' },
            { label: 'Historial', to: '/mas/historial', icon: History, color: 'text-text-dim' },
            { label: 'JA Lab', to: '/ja-lab', icon: FlaskConical, color: 'text-exact' },
          ].map(({ label, to, icon: Icon, color }) => (
            <Link key={label} to={to} className="card p-3 rounded-2xl flex flex-col items-center gap-1.5 text-center hover:border-brand/30 transition">
              <Icon className={cn('h-4 w-4', color)} />
              <span className="text-[9px] font-bold text-text-dim uppercase tracking-wide leading-tight">{label}</span>
            </Link>
          ))}
        </div>
      </section>

      {selectedPick && (
        <PickDetailSheet pick={selectedPick} onClose={() => setSelectedPick(null)} />
      )}
    </div>
  )
}
